import React, { ReactNode } from 'react';
import { Box, Divider, Link, Typography } from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import PageLayout from '../components/layout/PageLayout';
import { COMPANY, EMERGENCY, SUPPORT } from '../content/site';
import { BRAND } from '../styles/ui';

const linkSx = { color: BRAND.ink, fontWeight: 600, textDecorationColor: BRAND.lineStrong };

const Section: React.FC<{ title: string; children: ReactNode }> = ({ title, children }) => (
  <Box component="section">
    <Typography variant="h6" component="h2" sx={{ color: BRAND.ink, mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const Contact: React.FC = () => (
  <PageLayout
    title="Contact and help"
    subtitle="We're friendly and happy to help you set up Joan or answer any questions."
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Emergencies first */}
      <Box
        role="note"
        sx={{
          display: 'flex',
          gap: 2,
          p: 3,
          borderRadius: 1,
          backgroundColor: BRAND.warningSoft,
          border: `1px solid ${BRAND.line}`
        }}
      >
        <WarningAmberRoundedIcon sx={{ color: BRAND.warning, mt: 0.25 }} />
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
            If you are in danger, call 999.
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND.muted, mb: 1 }}>
            {EMERGENCY.silentSolution}
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND.muted }}>
            {EMERGENCY.helplineName}:{' '}
            <Link href={EMERGENCY.helplineHref} sx={linkSx}>{EMERGENCY.helplinePhone}</Link>
          </Typography>
        </Box>
      </Box>

      <Section title="Joan support">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <PhoneOutlinedIcon sx={{ color: BRAND.accent, mt: 0.5 }} />
            <Box>
              <Link href={SUPPORT.phoneHref} variant="h6" sx={linkSx}>{SUPPORT.phone}</Link>
              <Typography variant="body2" sx={{ color: BRAND.muted }}>{SUPPORT.hours}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <MailOutlineIcon sx={{ color: BRAND.accent, mt: 0.5 }} />
            <Box sx={{ minWidth: 0 }}>
              <Link href={`mailto:${SUPPORT.email}`} variant="h6" sx={{ ...linkSx, overflowWrap: 'anywhere' }}>{SUPPORT.email}</Link>
              <Typography variant="body2" sx={{ color: BRAND.muted }}>We reply on working days</Typography>
            </Box>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: BRAND.muted, mt: 3 }}>
          Call us if you need to change the device ID on your account, or if Joan tells you your device is already linked to another account.
        </Typography>
      </Section>

      <Divider sx={{ borderColor: BRAND.line }} />

      <Section title="Leaving this site quickly">
        <Typography variant="body2" sx={{ color: BRAND.muted }}>
          Every page has a Quick exit button at the top. It signs you out on this device and opens BBC Weather instead.
          It cannot clear your browsing history, so you may also want to use a private browsing window.
        </Typography>
      </Section>

      <Divider sx={{ borderColor: BRAND.line }} />

      <Section title="About us">
        <Typography variant="body2" sx={{ color: BRAND.muted }}>
          Joan is provided by {COMPANY.name}, registered in England and Wales (company no. {COMPANY.number}).
          Registered office: {COMPANY.registeredOffice}.
        </Typography>
      </Section>
    </Box>
  </PageLayout>
);

export default Contact;
